import { captureException, withScope } from '@sentry/browser';
import * as React from 'react';
import * as StackTrace from 'stacktrace-js';
import { ErrorModal } from './components/ErrorModal';

interface IState {
  errorDetails?: {
    error: Error;
    componentStack: string;
  };
}

interface IProps {
  children: React.ReactNode;
}

export default class ErrorBoundary extends React.Component<IProps, IState> {
  state: IState = {
    errorDetails: undefined,
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.state.errorDetails) {
      return;
    }
    withScope(async (scope) => {
      const errorInfoObj = {
        componentStack: errorInfo.componentStack,
      };

      scope.setExtras(errorInfoObj);
      captureException(error);

      const componentStackText = `:palm_tree: *Component Tree:*\n\`\`\`\n${cleanComponentStack(
        errorInfo.componentStack,
      )}\n\`\`\`\n\n\n\n`;

      const tsStacktrace = await getTypeScriptStacktrace(error);
      const stackText = `:books: *TS Stacktrace:*\n\`\`\`\n${tsStacktrace}\n\`\`\``;

      this.setState({
        errorDetails: {
          error,
          componentStack: componentStackText + stackText,
        },
      });
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.errorDetails) {
      return <ErrorModal errorDetails={this.state.errorDetails} onClose={this.handleRefresh} />;
    }
    return this.props.children;
  }
}

const getTypeScriptStacktrace = async (error: Error) => {
  const stackFrames = await StackTrace.fromError(error);
  return stackFrames
    .map(
      (sf) => `at ${sf.functionName} (${sf.fileName?.replace(/^.*swd-arnold-monorepo/, '').trim()}:${sf.lineNumber})`,
    )
    .join('\n');
};

const cleanComponentStack = (stack: string) => {
  return stack
    .split('\n')
    .map((line) => {
      const componentName = line.trim().split(' ')[1];
      if (!componentName || componentName.startsWith('http:') || componentName.startsWith('https:')) {
        return null;
      }
      return `at ${componentName}`;
    })
    .filter((line) => line != null)
    .join('\n');
};
